// 📄 src/pages/FichesPratiquesPage.jsx
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { fichesPratiquesFR, fichesPratiquesEN } from "../data/fichesPratiques";
import * as LucideIcons from "lucide-react";

// Une seule source de vérité pour les catégories et les slugs.
// Les libellés traduits restent dans UI, mais la structure ne peut plus diverger entre FR et EN.
const CATEGORY_CONFIG = {
  demarrage: {
    icon: "Compass",
    slugs: [
      "premiers-pas",
      "bien-remplir-fiche",
      "images-documents",
      "comprendre-valeurs",
      "confidentialite",
    ],
  },
  oeuvres: {
    icon: "Frame",
    slugs: ["ajouter-oeuvre", "modifier-oeuvre", "editions", "import-data"],
  },
  collections: {
    icon: "FolderOpen",
    slugs: ["collections", "tags-filtres", "emplacements", "rapports"],
  },
  technologies: {
    icon: "Sparkles",
    slugs: ["analyse-ia", "blockchain"],
  },
  estimation: {
    icon: "TrendingUp",
    slugs: ["estimation", "estimation-assurance"],
  },
  assurance: {
    icon: "Shield",
    slugs: ["assurance", "transport-oeuvres", "sinistre"],
  },
  conservation: {
    icon: "Wrench",
    slugs: ["conservation", "maintenance", "couts", "pret"],
  },
  transmission: {
    icon: "Users",
    slugs: [
      "succession",
      "ventes-donations",
      "proposer-institution",
      "proposer-auction",
      "dation-paiement",
    ],
  },
};

const START_HERE_SLUGS = [
  "premiers-pas",
  "ajouter-oeuvre",
  "images-documents",
  "bien-remplir-fiche",
  "rapports",
];

const FicheIcon = ({ iconName, className = "" }) => {
  const IconComponent = LucideIcons[iconName] || LucideIcons.HelpCircle;
  return <IconComponent className={className} aria-hidden="true" />;
};

const FicheBadges = ({ fiche, UI }) => {
  const badges = [];

  if (fiche.access === "premium") badges.push(UI.badges.premium);
  if (fiche.service === "accompanied") badges.push(UI.badges.accompanied);
  if (fiche.status === "beta") badges.push(UI.badges.beta);

  if (!badges.length) return null;

  return (
    <div className="flex flex-wrap gap-2 mb-4">
      {badges.map((badge) => (
        <span
          key={badge}
          className="inline-flex items-center border border-gray-300 px-2 py-1 text-[11px] font-medium uppercase tracking-wide text-gray-600"
        >
          {badge}
        </span>
      ))}
    </div>
  );
};

const FicheCard = ({ fiche, UI, featured = false, index }) => (
  <Link
    to={`/fiche/${fiche.slug}`}
    className={`bg-white hover:bg-gray-50 transition-colors group ${
      featured ? "p-6 border border-gray-200" : "p-8"
    }`}
  >
    {featured && typeof index === "number" ? (
      <div className="text-xs font-semibold tracking-[0.18em] text-gray-400 mb-5">
        {String(index + 1).padStart(2, "0")}
      </div>
    ) : null}

    <div className="mb-4">
      <FicheIcon
        iconName={fiche.icon}
        className="w-8 h-8 text-gray-900 group-hover:text-gray-600 transition-colors"
      />
    </div>

    <FicheBadges fiche={fiche} UI={UI} />

    <h3 className="font-serif text-xl font-semibold text-gray-900 mb-2 group-hover:text-gray-600 transition-colors">
      {fiche.title}
    </h3>
    <p className="text-gray-600 text-sm leading-relaxed">{fiche.description}</p>
  </Link>
);

const FichesPratiquesPage = () => {
  const { i18n } = useTranslation();
  const lang = (i18n?.language || "fr").slice(0, 2);

  const dataset = useMemo(() => {
    if (lang !== "en") return fichesPratiquesFR;

    const merged = new Map(fichesPratiquesFR.map((fiche) => [fiche.slug, { ...fiche }]));
    fichesPratiquesEN.forEach((englishFiche) => {
      const frenchFallback = merged.get(englishFiche.slug) || {};
      merged.set(englishFiche.slug, { ...frenchFallback, ...englishFiche });
    });
    return Array.from(merged.values());
  }, [lang]);

  const UI = useMemo(() => {
    const categoryTitles =
      lang === "en"
        ? {
            demarrage: "Getting started",
            oeuvres: "Artwork management",
            collections: "Collections & organisation",
            technologies: "AI & traceability",
            estimation: "Appraisals & valuation",
            assurance: "Insurance & protection",
            conservation: "Conservation & maintenance",
            transmission: "Sale & transfer",
          }
        : {
            demarrage: "Bien démarrer",
            oeuvres: "Gestion des œuvres",
            collections: "Collections & organisation",
            technologies: "IA & traçabilité",
            estimation: "Estimations & valorisation",
            assurance: "Assurance & protection",
            conservation: "Conservation & maintenance",
            transmission: "Vente & transmission",
          };

    const categories = Object.fromEntries(
      Object.entries(CATEGORY_CONFIG).map(([key, category]) => [
        key,
        { ...category, title: categoryTitles[key] },
      ])
    );

    if (lang === "en") {
      return {
        pageTitle: "How-to Guides",
        pageSubtitle: "Clear guidance for documenting, protecting, and managing your collection.",
        startTitle: "Start here",
        startSubtitle: "Five practical steps to obtain a useful first inventory without completing everything at once.",
        searchPlaceholder: "Search a guide…",
        resultsOne: "result found",
        resultsMany: "results found",
        noResult: "No guide matches your search",
        allBtn: "All guides",
        badges: {
          premium: "Premium",
          accompanied: "Guided service",
          beta: "Beta",
        },
        categories,
      };
    }

    return {
      pageTitle: "Fiches pratiques",
      pageSubtitle: "Des repères clairs pour documenter, protéger et gérer votre collection.",
      startTitle: "Commencez ici",
      startSubtitle: "Cinq étapes concrètes pour obtenir rapidement un premier inventaire utile, sans devoir tout compléter d'un coup.",
      searchPlaceholder: "Rechercher une fiche pratique…",
      resultsOne: "résultat trouvé",
      resultsMany: "résultats trouvés",
      noResult: "Aucune fiche ne correspond à votre recherche",
      allBtn: "Toutes les fiches",
      badges: {
        premium: "Premium",
        accompanied: "Service accompagné",
        beta: "Bêta",
      },
      categories,
    };
  }, [lang]);

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");

  const ficheBySlug = useMemo(
    () => new Map(dataset.map((fiche) => [fiche.slug, fiche])),
    [dataset]
  );

  const startHereFiches = useMemo(
    () => START_HERE_SLUGS.map((slug) => ficheBySlug.get(slug)).filter(Boolean),
    [ficheBySlug]
  );

  const groupedFiches = useMemo(() => {
    const grouped = {};
    Object.entries(UI.categories).forEach(([key, category]) => {
      grouped[key] = category.slugs.map((slug) => ficheBySlug.get(slug)).filter(Boolean);
    });
    return grouped;
  }, [ficheBySlug, UI.categories]);

  const filteredFiches = useMemo(() => {
    let filtered = dataset;

    if (selectedCategory !== "all") {
      const allowedSlugs = new Set(UI.categories[selectedCategory]?.slugs || []);
      filtered = filtered.filter((fiche) => allowedSlugs.has(fiche.slug));
    }

    const normalizedQuery = searchQuery.trim().toLowerCase();
    if (!normalizedQuery) return filtered;

    return filtered.filter((fiche) => {
      const searchableContent = [
        fiche.title,
        fiche.description,
        ...(fiche.steps || []),
        ...(fiche.tips || []),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchableContent.includes(normalizedQuery);
    });
  }, [dataset, selectedCategory, searchQuery, UI.categories]);

  const showSearchResults = searchQuery.trim().length > 0;

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-7xl mx-auto px-6 py-12">
        <header className="mb-12 text-center">
          <h1 className="font-serif text-5xl font-semibold text-gray-900 mb-3">
            {UI.pageTitle}
          </h1>
          <p className="text-lg text-gray-600">{UI.pageSubtitle}</p>
        </header>

        {!showSearchResults && selectedCategory === "all" && (
          <section className="mb-14" aria-labelledby="start-here-title">
            <div className="bg-gray-950 text-white px-7 py-8 md:px-10 md:py-10 mb-px">
              <div className="max-w-3xl">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gray-400 mb-3">
                  ARTKIV
                </p>
                <h2 id="start-here-title" className="font-serif text-3xl md:text-4xl font-semibold mb-3">
                  {UI.startTitle}
                </h2>
                <p className="text-gray-300 leading-relaxed">{UI.startSubtitle}</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-px bg-gray-200">
              {startHereFiches.map((fiche, index) => (
                <FicheCard key={fiche.slug} fiche={fiche} UI={UI} featured index={index} />
              ))}
            </div>
          </section>
        )}

        <div className="mb-10 max-w-2xl mx-auto">
          <div className="relative">
            <LucideIcons.Search
              className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"
              aria-hidden="true"
            />
            <input
              type="search"
              aria-label={UI.searchPlaceholder}
              placeholder={UI.searchPlaceholder}
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              className="w-full pl-12 pr-4 py-4 border border-gray-300 focus:border-gray-900 focus:ring-1 focus:ring-gray-900 text-gray-900 placeholder-gray-400 transition-colors rounded-none"
            />
          </div>
          {showSearchResults && (
            <p className="text-gray-600 text-sm mt-3 text-center" aria-live="polite">
              {filteredFiches.length} {filteredFiches.length > 1 ? UI.resultsMany : UI.resultsOne}
            </p>
          )}
        </div>

        <nav className="mb-12 border-t border-b border-gray-200 py-6" aria-label={UI.pageTitle}>
          <div className="flex flex-wrap justify-center gap-2">
            <button
              type="button"
              onClick={() => setSelectedCategory("all")}
              className={`px-5 py-2 text-sm font-medium transition-colors rounded-none ${
                selectedCategory === "all"
                  ? "bg-black text-white"
                  : "text-gray-700 hover:text-black border border-gray-300 hover:border-black"
              }`}
            >
              {UI.allBtn}
            </button>

            {Object.entries(UI.categories).map(([key, category]) => (
              <button
                type="button"
                key={key}
                onClick={() => setSelectedCategory(key)}
                className={`px-5 py-2 text-sm font-medium transition-colors rounded-none ${
                  selectedCategory === key
                    ? "bg-black text-white"
                    : "text-gray-700 hover:text-black border border-gray-300 hover:border-black"
                }`}
              >
                {category.title}
              </button>
            ))}
          </div>
        </nav>

        {showSearchResults ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-gray-200">
            {filteredFiches.length ? (
              filteredFiches.map((fiche) => <FicheCard key={fiche.slug} fiche={fiche} UI={UI} />)
            ) : (
              <div className="col-span-full bg-white p-12 text-center">
                <p className="text-gray-500">{UI.noResult}</p>
              </div>
            )}
          </div>
        ) : selectedCategory !== "all" ? (
          <section>
            <div className="flex items-center justify-center gap-3 mb-8">
              <FicheIcon
                iconName={UI.categories[selectedCategory].icon}
                className="w-8 h-8 text-gray-900"
              />
              <h2 className="font-serif text-3xl font-semibold text-gray-900">
                {UI.categories[selectedCategory].title}
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-gray-200">
              {groupedFiches[selectedCategory].map((fiche) => (
                <FicheCard key={fiche.slug} fiche={fiche} UI={UI} />
              ))}
            </div>
          </section>
        ) : (
          <div className="space-y-16">
            {Object.entries(UI.categories).map(([key, category]) => (
              <section key={key} aria-labelledby={`category-${key}`}>
                <div className="mb-8 pb-4 border-b border-gray-200">
                  <div className="flex items-center gap-3">
                    <FicheIcon iconName={category.icon} className="w-8 h-8 text-gray-900" />
                    <h2 id={`category-${key}`} className="font-serif text-3xl font-semibold text-gray-900">
                      {category.title}
                    </h2>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-gray-200">
                  {groupedFiches[key].map((fiche) => (
                    <FicheCard key={fiche.slug} fiche={fiche} UI={UI} />
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FichesPratiquesPage;
